import React from 'react';
import { Modal, Box, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotesIcon from '@mui/icons-material/Notes';

const EventDetailsModal = ({ open, onClose, event }) => {
    if (!event) return null;

    // Formatting
    const start = event.start.dateTime ? new Date(event.start.dateTime) : new Date(event.start.date);
    const end = event.end.dateTime ? new Date(event.end.dateTime) : new Date(event.end.date);
    const isAllDay = !event.start.dateTime;

    const dateStr = start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
    const timeStr = isAllDay 
        ? 'All Day' 
        : `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;

    const bgColor = event.calendarColor || '#4fd1c5'; 

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="event-details-title"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)'
            }}
        >
            <Box sx={{
                width: { xs: '90%', md: '500px' },
                bgcolor: '#ffffff', // Light Theme Background
                color: '#2d3748',   // Dark Text
                borderRadius: 4,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden',
                outline: 'none',
                position: 'relative'
            }}>
                {/* Decorative Color Strip */}
                <Box sx={{ height: '12px', bgcolor: bgColor, width: '100%' }} />

                {/* Header */}
                <Box sx={{ p: 4, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h3" fontWeight="900" sx={{ lineHeight: 1.1, fontSize: '2rem' }}>
                        {event.summary}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ 
                        color: '#a0aec0', 
                        mt: -1, mr: -2,
                        '&:hover': { color: '#2d3748', bgcolor: '#f7fafc' } 
                    }}>
                        <CloseIcon fontSize="large" />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4, pt: 2 }}>
                    {/* Time */}
                    <Box display="flex" gap={2} mb={3} alignItems="center">
                        <AccessTimeIcon sx={{ color: bgColor, fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#2d3748' }}>{dateStr}</Typography>
                            <Typography variant="h6" color="#718096" fontWeight="500">{timeStr}</Typography>
                        </Box>
                    </Box>

                    {/* Location */}
                    {event.location && (
                        <Box display="flex" gap={2} mb={3} alignItems="center">
                            <LocationOnIcon sx={{ color: bgColor, fontSize: '2rem' }} />
                            <Typography variant="h6" sx={{ color: '#4a5568' }}>{event.location}</Typography>
                        </Box>
                    )}

                    {/* Calendar Badge */}
                    <Box display="flex" gap={2} mb={3} alignItems="center">
                        <CalendarTodayIcon sx={{ color: bgColor, fontSize: '2rem' }} />
                        <Box sx={{ 
                            display: 'inline-block', 
                            bgcolor: `${bgColor}20`, // 20% opacity matching color
                            px: 1.5, py: 0.5, borderRadius: 2 
                        }}>
                             <Typography variant="subtitle2" fontWeight="bold" sx={{ color: bgColor, textTransform: 'uppercase' }}>
                                {event.calendarSummary}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Description */}
                    {event.description && (
                         <Box display="flex" gap={2} mt={3} sx={{ bgcolor: '#f7fafc', p: 2, borderRadius: 2 }}>
                            <NotesIcon sx={{ color: '#a0aec0', mt: 0.5 }} />
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#4a5568' }} dangerouslySetInnerHTML={{ __html: event.description }} />
                        </Box>
                    )}

                    <Box mt={5} display="flex" justify="flex-end">
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={onClose} 
                            sx={{ 
                                bgcolor: '#edf2f7', 
                                color: '#2d3748', 
                                boxShadow: 'none',
                                fontWeight: 'bold',
                                py: 1.5,
                                fontSize: '1.1rem',
                                '&:hover': { bgcolor: '#e2e8f0', boxShadow: 'none' }
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default EventDetailsModal;
